import type { AiCapabilityPage } from "../schema.js";
import { LIVE, ALL_ROLES } from "./_shared.js";

/**
 * `INVITE-ACCEPT` (@UC0214) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/my-invitations` (ADR-0009 bất biến 5).
 * Cửa mobile trang này chịu trách nhiệm (xem lời mời · xem trước nhóm · nhận lời mời). *
 * `redirect` vì người nhận mời **chưa thuộc nhóm** — cổng ghi của trợ lý đọc nhóm hiệu lực, mà ở đây chưa
 * có nhóm nào. Và vì nhận mời có thể kéo theo bước CHỌN DANH TÍNH ("bạn là Minh nào?") — ca ≥2 ứng viên là
 * ca THƯỜNG, không phải ngoại lệ (`IDENTITY-LINK`, @UC0215) — nên GUI dẫn trọn vòng là đúng chỗ.
 */
export const INVITE_ACCEPT = {
  id: "INVITE-ACCEPT",
  uc: "@UC0214",
  tier: "redirect",
  status: LIVE,
  roles: ALL_ROLES,
  flags: [],
  slots: [],
  llm: {
    description:
      "Lời mời NGƯỜI KHÁC GỬI CHO CHÍNH TÔI: xem có ai mời mình vào nhóm nào chưa, rồi nhận hoặc từ chối. Chiều của câu là NGƯỜI KHÁC → TÔI ('ai mời tôi', 'có ai mời mình vào nhóm nào chưa'). Nếu chiều ngược lại — người ngoài xin vào nhóm của TÔI và tôi đi duyệt họ — thì đó là GRP-JOIN-REQUEST. Và nếu tôi là người ĐI TẠO/THU HỒI một LINK mời để phát cho người khác, kể cả khi hỏi link đó còn hiệu lực hay đã hết hạn, thì đó là INVITE-LINK-MANAGE — trang này chỉ nói về lời mời đích danh đang nằm trong hộp thư của tôi.",
    examples: [
      "tôi có lời mời nào không",
      "toi co loi moi nao khong",
      "ai mời tôi vào nhóm",
      "ai moi toi vao nhom",
      "nhận lời mời",
      "nhan loi moi",
      "vào nhóm được mời",
      "từ chối lời mời",
      "tu choi loi moi",
      "lời mời",
      "xem lời mời",
      "nhận lời mời join nhóm",
      /*
       * 🔴 0101 — đối trọng cho câu KHÔNG DẤU. Đo ba lần thu liên tiếp: `"ai moi toi vao nhom"` luôn
       * rơi sang `GRP-JOIN-REQUEST`, trong khi bản CÓ DẤU `"ai mời tôi vào nhóm"` luôn đúng. Không
       * dấu thì `moi` = mời|mới và `toi` = tôi|tối, nên chỉ còn mẫu chữ `"ai … vao nhom"` — mẫu ấy
       * nghiêng về trang duyệt-người-xin-vào. Ba câu dưới dạy Sổ cái hướng "lời mời gửi CHO TÔI".
       */
      "ai gui loi moi cho toi",
      "toi duoc ai moi",
      "loi moi cua toi",
      // ——— 0105: thêm cho đủ 40 ———
      "vao nhom duoc moi",
      "xem loi moi",
      "nhan loi moi join nhom",
      "loi moi",
      "hộp thư lời mời của tôi",
      "xem cac loi moi vao nhom cua toi",
      "có ai mời mình vào nhóm nào chưa",
      "co ai moi minh vao nhom nao chua",
      "mình được mời vào nhóm nào vậy",
      "minh duoc moi vao nhom nao vay",
      "chấp nhận lời mời vào nhóm",
      "chap nhan loi moi vao nhom",
      "đồng ý lời mời",
      "dong y loi moi",
      /*
       * 🔴 0105 vòng 2 — HAI CẶP BỊ THAY, cả hai do vòng 1 đưa vào, và cả hai hỏng ở **bản KHÔNG DẤU**
       * đúng như cái bẫy mà chú thích 0101 ngay trên đã mô tả:
       *
       *   · `"xem truoc nhom duoc moi roi quyet"` → `READ-FUND-MONEY` (conf **0,2** — model tự khai là
       *     đang đoán). Bỏ dấu thì *"xem truoc nhom"* chỉ còn mẫu "xem … nhóm", và chữ `quyet` mất hẳn
       *     nghĩa *quyết định*. Bản CÓ DẤU vẫn đúng ⇒ hỏng ở câu mẫu, không ở mô tả.
       *   · `"loi moi con hieu luc khong"` → `INVITE-LINK-MANAGE`. *"còn hiệu lực"* là từ vựng của
       *     LINK MỜI (link hết hạn), không phải của một lời mời gửi đích danh cho tôi. Ở đây ranh giới
       *     là THẬT nên mô tả CẢ HAI trang cũng được siết (xem mô tả), nhưng câu mẫu vẫn phải rời đi:
       *     nó đang dạy Sổ rằng từ vựng của trang kia thuộc về trang này.
       *
       * Thay bằng hai cặp giữ nguyên chiều "lời mời gửi CHO TÔI" và không mượn từ vựng trang nào.
       */
      "xem thử nhóm mời tôi rồi mới nhận",
      "xem thu nhom moi toi roi moi nhan",
      "lời mời tôi nhận được còn nhận được nữa không",
      "loi moi toi nhan duoc con nhan duoc nua khong",
      "bỏ qua lời mời này",
      "bo qua loi moi nay",
      "tôi nhận được lời mời nhưng chưa bấm",
      "toi nhan duoc loi moi nhung chua bam",
      "mở danh sách lời mời của mình",
      "mo danh sach loi moi cua minh",
      "nhận lời mời vào nhóm phụ huynh",
      "nhan loi moi vao nhom phu huynh",
    ],
  },
  screen: "/my-invitations",
  screenShows: "my_invitations",
  endpoints: [
    "GET /me/invitations",
    "POST /groups/{groupId}/invitations/accept",
    "POST /groups/{groupId}/invitations/preview",
  ],
  facts: [],
  templates: {
    // US-AIV2-tourguide-0008 (QĐ PO 05/10 «sửa câu cho đúng ngay»): màn lời mời CHỈ ĐỌC — nhận bằng link trong email/tin nhắn mời (khảo sát app 05/10).
    redirect: "Mình mở danh sách lời mời của bạn nhé. Xem tên nhóm và người mời trước — muốn nhận, bạn mở link trong email hoặc tin nhắn mời. Vào nhóm là nhận luôn nghĩa vụ đóng góp của nhóm đó.",
  },
  journey: { after: ["GRP-OPEN", "READ-PEOPLE-MINE"] },
  help: { title: "Lời mời của tôi", tryNow: "tôi có lời mời nào không" },
} satisfies AiCapabilityPage;
